import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { Server } from '../apollo/types';
import { logger } from '../datadog/logger';
import { formDataByPatientShortIdAtom } from './client-information/state';
import { QuestionTypeEnum } from './client-information/types';

export type GetPatientFormsByPatientShortIdVariables = {
  patientShortId?: string;
};

export type GetPatientFormsByPatientShortIdQueryData = {
  patientChart?: {
    __typename?: 'PatientObject';
    id: string;
    shortId: string;
    clientUser: {
      __typename?: 'ClientUserObject';
      clientUserShortId: string;
    };
    formResponses: Array<{
      __typename?: 'FormResponseObject';
      id: string;
      formId: string;
      highAcuity: boolean;
      reviewedAt?: unknown | null;
      formType: string;
      createdAt: string;
      score: number;
      formResponseModel: {
        pages: {
          elements: {
            name: string;
            value: string;
          }[];
        }[];
      };
    }>;
    allProviderForms: Array<{
      formId: string;
      formShortId: string;
      formTitle: string;
      formVersion: number;
      formType: string;
      formModel: {
        formType: string;
        formPrompt: string;
        formTitle: string;
        pages: {
          sectionTitle: string;
          elements: {
            name: string;
            required: boolean;
            type: QuestionTypeEnum;
            title: string;
            maxLength?: number | null;
            visibleIf?: string | null;
            layout?: string | null;
            placeholder?: string | null;
            options: {
              value: string;
              label: string;
            }[];
          }[];
        }[];
      };
    }>;
    lastSubmittedClaim?: {
      __typename?: 'ClaimObject';
    } | null;
  } | null;
};

export const GET_PATIENT_FORMS_BY_PATIENT_SHORT_ID = gql`
  query GetPatientFormsByPatientShortId($patientShortId: ID!) {
    patientChart(values: { patientShortId: $patientShortId }) {
      id
      shortId
      clientUser {
        clientUserShortId
      }
      latestFormSubmissionRequests {
        formId
        createdAt
        daysSinceSubmissionRequest
      }
      formResponses {
        id
        formId
        formType
        score
        highAcuity
        createdAt
        reviewedAt
        formResponseModel {
          pages {
            elements {
              name
              value
            }
          }
        }
      }
      allProviderForms {
        formId
        formShortId
        formTitle
        formType
        formVersion
        formModel {
          formTitle
          formPrompt
          pages {
            sectionTitle
            elements {
              type
              placeholder
              maxLength
              visibleIf
              layout
              name
              title
              required
              options {
                value
                label
              }
            }
          }
        }
      }
    }
  }
`;

const useSetPatientFormsDataCallback = () => {
  const setFormDataForPatient = useSetAtom(formDataByPatientShortIdAtom);

  return useCallback(
    (data: GetPatientFormsByPatientShortIdQueryData) => {
      const patientChart = data?.patientChart;
      if (!patientChart) return;
      const patientShortId = patientChart?.shortId;
      setFormDataForPatient((prev) => ({
        ...prev,
        [patientShortId]: {
          ...prev[patientShortId],
          formResponses: patientChart?.formResponses ?? [],
          providerForms: patientChart?.allProviderForms ?? [],
        },
      }));
    },
    [setFormDataForPatient],
  );
};

export function useGetFormsByPatientShortId(patientShortId: string) {
  const formDataByPatient = useAtomValue(formDataByPatientShortIdAtom);
  // This is an intensive query, so we skip it if we already have the data we need
  const skip = !patientShortId || !!formDataByPatient[patientShortId];

  const onCompleted = useSetPatientFormsDataCallback();
  // TODO CARED-2858 start returning the query once we've fixed the unnecessary re-fetching of forms
  useQuery<GetPatientFormsByPatientShortIdQueryData, GetPatientFormsByPatientShortIdVariables>(
    GET_PATIENT_FORMS_BY_PATIENT_SHORT_ID,
    {
      variables: { patientShortId },
      context: { server: Server.MONOLITH },
      skip,
      notifyOnNetworkStatusChange: true,
      onCompleted,
      onError: (error) => {
        logger.error('Unable to fetch patient forms', { patientShortId }, error);
      },
    },
  );
}

export function useLazyGetFormsByPatientShortId() {
  const onCompleted = useSetPatientFormsDataCallback();
  return useLazyQuery<GetPatientFormsByPatientShortIdQueryData, GetPatientFormsByPatientShortIdVariables>(
    GET_PATIENT_FORMS_BY_PATIENT_SHORT_ID,
    {
      context: { server: Server.MONOLITH },
      notifyOnNetworkStatusChange: true,
      onCompleted,
      onError: (error) => {
        logger.error('Unable to fetch patient forms', {}, error);
      },
    },
  );
}
